import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pause, Play, Square, Clock, AlertTriangle } from 'lucide-react';
import { useTimerActivo, usePausarTimer, useReanudarTimer, useDetenerTimer } from '@/hooks/queries/useTiempo';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Format live elapsed time using server-reported minutos_transcurridos as base,
 * then ticking up from the last fetch. This correctly handles pause/resume cycles
 * because the server already subtracts paused duration from minutos_transcurridos.
 */
function formatLiveTime(baseMinutes: number, fetchedAt: number, pausado: boolean): string {
  if (pausado) {
    const h = Math.floor(baseMinutes / 60);
    const m = baseMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  // Add seconds elapsed since the last server fetch
  const secondsSinceFetch = Math.max(0, Math.floor((Date.now() - fetchedAt) / 1000));
  const totalSec = baseMinutes * 60 + secondsSinceFetch;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h > 0 ? `${h}:` : ''}${h > 0 ? String(m).padStart(2, '0') : m}:${String(s).padStart(2, '0')}`;
}

export function TimerGlobal() {
  const { data: timer, isError, dataUpdatedAt } = useTimerActivo();
  const pausar = usePausarTimer();
  const reanudar = useReanudarTimer();
  const detener = useDetenerTimer();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [, setTick] = useState(0);
  // Track the timestamp when timer data was last fetched
  const fetchedAtRef = useRef(Date.now());

  // Update fetchedAt whenever the query data changes
  useEffect(() => {
    if (dataUpdatedAt) {
      fetchedAtRef.current = dataUpdatedAt;
    }
  }, [dataUpdatedAt]);

  // Tick every second for live counter when timer is active and not paused
  useEffect(() => {
    if (!timer?.activo || timer.pausado) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [timer?.activo, timer?.pausado]);

  // Show error indicator if timer query fails (user needs to know their timer state is unknown)
  if (isError) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-destructive/20 bg-destructive/5 px-2 py-1">
        <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
        <span className="text-xs text-destructive">Timer: error</span>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['tiempo', 'timer'] })}
          className="text-xs text-destructive underline hover:no-underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!timer?.activo) return null;

  const timeStr = formatLiveTime(timer.minutos_transcurridos, fetchedAtRef.current, timer.pausado);
  const isPending = pausar.isPending || reanudar.isPending || detener.isPending;

  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2 py-1">
      <Clock className="h-3.5 w-3.5 text-primary animate-pulse" />
      <button
        onClick={() => timer.actividad_id && navigate(`/actividades/${timer.actividad_id}`)}
        className="text-xs font-medium text-primary hover:underline max-w-[120px] truncate"
        title={timer.actividad_titulo ?? undefined}
      >
        {timer.actividad_titulo ?? 'Timer'}
      </button>
      <span className="text-xs font-mono font-semibold text-primary tabular-nums">
        {timeStr}
      </span>
      <div className="flex items-center gap-0.5 ml-0.5">
        {timer.pausado ? (
          <button
            onClick={() => reanudar.mutate()}
            disabled={isPending}
            className="rounded p-0.5 text-primary hover:bg-primary/10 disabled:opacity-50"
            title="Reanudar"
          >
            <Play className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            onClick={() => pausar.mutate()}
            disabled={isPending}
            className="rounded p-0.5 text-primary hover:bg-primary/10 disabled:opacity-50"
            title="Pausar"
          >
            <Pause className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => detener.mutate()}
          disabled={isPending}
          className="rounded p-0.5 text-destructive hover:bg-destructive/10 disabled:opacity-50"
          title="Detener y registrar"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
